import { JsonSchemaBasicObject, JsonSchemaEnum, JsonSchemaInteger } from '@evidentid/json-schema/interfaces/JsonSchema';

export const RECOLLECTION_INTERVAL_CRITERION_FIELD = 'coverage.validUntil';
export const RECOLLECTION_INTERVAL_REFERENCE = 'recollectionInterval';
export const RECOLLECTION_INTERVAL_VALUE_PROPERTY = 'value';
export const RECOLLECTION_INTERVAL_UNIT_PROPERTY = 'unit';

export const RECOLLECTION_INTERVAL_UNITS = [ 'DAYS', 'MONTHS', 'YEARS' ] as const;

export type RecollectionIntervalUnit = typeof RECOLLECTION_INTERVAL_UNITS[number];

export const DEFAULT_RECOLLECTION_INTERVAL_UNIT: RecollectionIntervalUnit = 'DAYS';

export interface RecollectionIntervalBounds {
    minimum: number | null;
    maximum: number | null;
}

export function isRecollectionIntervalUnit(value: unknown): value is RecollectionIntervalUnit {
    return RECOLLECTION_INTERVAL_UNITS.includes(value as RecollectionIntervalUnit);
}

/**
 * Resolves the unit the bounds apply to, falling back to the default when the reference value has no
 * unit yet or an unrecognised one, so the select box never renders blank.
 */
export function getRecollectionIntervalUnit(intervalValue: unknown): RecollectionIntervalUnit {
    const unit = (intervalValue as Record<string, unknown> | null | undefined)
        ?.[RECOLLECTION_INTERVAL_UNIT_PROPERTY];
    return isRecollectionIntervalUnit(unit) ? unit : DEFAULT_RECOLLECTION_INTERVAL_UNIT;
}

/**
 * The noun used for the unit in the validation message, taken from the schema's enumLabels so the
 * wording follows whatever BE labels the units with.
 */
export function getRecollectionIntervalUnitNoun(
    referenceSchema: JsonSchemaBasicObject,
    unit: RecollectionIntervalUnit,
): string {
    const unitSchema = referenceSchema.properties?.[RECOLLECTION_INTERVAL_UNIT_PROPERTY] as
        JsonSchemaEnum | undefined;
    const index = unitSchema?.enum ? unitSchema.enum.indexOf(unit) : -1;
    const label = index >= 0 ? unitSchema?.enumLabels?.[index] : undefined;
    return String(label || unit).toLowerCase();
}

/**
 * Whether an `allOf` branch's `if` selects the given unit.
 *
 * BE states the condition as a single valued `enum`; `const` says exactly the same thing, so both are
 * accepted rather than letting the message lose the bound if BE switches between them. A branch whose
 * enum lists several units applies to each of them.
 */
function doesBranchApplyToUnit(branch: any, unit: RecollectionIntervalUnit): boolean {
    const unitCondition = branch?.if?.properties?.[RECOLLECTION_INTERVAL_UNIT_PROPERTY];
    return unitCondition?.const === unit
        || (Array.isArray(unitCondition?.enum) && unitCondition.enum.includes(unit));
}

/**
 * The maximum the `allOf` branches impose on this unit, or null when no branch applies to it.
 *
 * Every branch whose `if` matches contributes its `then`, so when more than one applies the value has
 * to satisfy all of them and the effective bound is the tightest - taking the last one read would name
 * a limit ajv does not enforce.
 *
 * Kept separate from getRecollectionIntervalBounds because that reader falls back to a flat maximum on
 * the value property, which is not tied to any unit. Anything phrased in terms of a specific unit must
 * read the branch, or a flat `maximum: 99` would be reported as the bound for every unit.
 */
function getRecollectionIntervalBranchMaximum(
    referenceSchema: JsonSchemaBasicObject,
    unit: RecollectionIntervalUnit,
): number | null {
    const conditionalBranches: any[] = (referenceSchema as any).allOf || [];
    return conditionalBranches.reduce<number | null>((result, branch) => {
        const branchMaximum = branch?.then?.properties?.[RECOLLECTION_INTERVAL_VALUE_PROPERTY]?.maximum;
        if (!doesBranchApplyToUnit(branch, unit) || typeof branchMaximum !== 'number') {
            return result;
        }
        return result === null ? branchMaximum : Math.min(result, branchMaximum);
    }, null);
}

/**
 * Reads the bounds that apply to the period value for the given unit.
 *
 * The maximum depends on the sibling unit, so BE expresses it as an `allOf` of `if`/`then` pairs
 * rather than on `properties.value`. Reading it back from there keeps the message honest if BE ever
 * changes a bound; a flat `maximum` on the value property is still honoured as a fallback.
 */
export function getRecollectionIntervalBounds(
    referenceSchema: JsonSchemaBasicObject,
    unit: RecollectionIntervalUnit,
): RecollectionIntervalBounds {
    const valueSchema = referenceSchema.properties?.[RECOLLECTION_INTERVAL_VALUE_PROPERTY] as
        JsonSchemaInteger | undefined;
    const branchMaximum = getRecollectionIntervalBranchMaximum(referenceSchema, unit);
    const flatMaximum = typeof valueSchema?.maximum === 'number' ? valueSchema.maximum : null;

    return {
        minimum: typeof valueSchema?.minimum === 'number' ? valueSchema.minimum : null,
        maximum: branchMaximum === null ? flatMaximum : branchMaximum,
    };
}

/**
 * The message shown when the value and unit together fall outside the schema's bounds. Also covers a
 * missing or fractional value, since "a whole number ..." states the requirement either way.
 */
export function buildRecollectionIntervalRangeError(
    referenceSchema: JsonSchemaBasicObject,
    unit: RecollectionIntervalUnit,
): string {
    const noun = getRecollectionIntervalUnitNoun(referenceSchema, unit);
    const { minimum, maximum } = getRecollectionIntervalBounds(referenceSchema, unit);

    let range = '';
    if (minimum !== null && maximum !== null) {
        range = ` between ${minimum} and ${maximum}`;
    } else if (maximum !== null) {
        range = ` no greater than ${maximum}`;
    } else if (minimum !== null) {
        range = ` of at least ${minimum}`;
    }

    // Naming the overall cap helps when the selected unit hides it, but repeating it when the unit is
    // already years would read as a repetition. Read from the YEARS branch specifically: a flat maximum
    // on the value property is not a years bound and must not be presented as one.
    const yearsMaximum = unit === 'YEARS'
        ? null
        : getRecollectionIntervalBranchMaximum(referenceSchema, 'YEARS');
    const overallCap = yearsMaximum === null
        ? ''
        : ` (${yearsMaximum} ${getRecollectionIntervalUnitNoun(referenceSchema, 'YEARS')} maximum)`;

    return `Enter a whole number of ${noun}${range}${overallCap}.`;
}

export const RECOLLECTION_INTERVAL_MODE_PROPERTY = 'mode';

export const RECOLLECTION_INTERVAL_MODES = [ 'RECURRING_CADENCE', 'CUSTOM_DATE_PER_SUBMISSION' ] as const;

export type RecollectionIntervalMode = typeof RECOLLECTION_INTERVAL_MODES[number];

export function isRecollectionIntervalMode(value: unknown): value is RecollectionIntervalMode {
    return RECOLLECTION_INTERVAL_MODES.includes(value as RecollectionIntervalMode);
}

/**
 * The configured mode, or null when nothing has been configured yet so no radio is preselected.
 *
 * A value carrying a period but no mode is read as a cadence, matching how the API backfills them.
 */
export function getRecollectionIntervalMode(intervalValue: unknown): RecollectionIntervalMode | null {
    const interval = intervalValue as Record<string, unknown> | null | undefined;
    const mode = interval?.[RECOLLECTION_INTERVAL_MODE_PROPERTY];
    if (isRecollectionIntervalMode(mode)) {
        return mode;
    }
    const hasLegacyPeriod = interval?.[RECOLLECTION_INTERVAL_VALUE_PROPERTY] !== undefined
        || interval?.[RECOLLECTION_INTERVAL_UNIT_PROPERTY] !== undefined;
    return hasLegacyPeriod ? 'RECURRING_CADENCE' : null;
}
